import { motion, AnimatePresence } from 'framer-motion';

// Components
import IconButton from '../../atoms/IconButton/IconButton';

// Utils
import cn from '../../../utils/cn';

// Styles
import styles from './TinyMceModal.module.css';

/**
 * Animation variants for backdrop
 * @type {import('framer-motion').Variants}
 */
const backdropVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeIn',
    },
  },
};

/**
 * Animation variants for modal
 * @type {import('framer-motion').Variants}
 */
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -10,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.6, 1],
    },
  },
};

/**
 * TinyMceModal - Specialized modal for TinyMCE editor
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Function to call when modal is closed
 * @param {React.ReactNode} props.actionButtons - Action buttons
 * @param {React.ReactNode} props.children - Modal content (TinyMCE editor)
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element | null} TinyMceModal component
 */
function TinyMceModal({
  isOpen,
  onClose,
  actionButtons,
  children,
  className = '',
}) {
  /**
   * Handle keyboard events
   * @param {React.KeyboardEvent} e - Keyboard event
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  /**
   * Handle backdrop click
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className={styles.backdrop}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          role="presentation"
        >
          <motion.div
            className={cn([styles.modal, className])}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button - top right */}
            <IconButton
              icon="close-md"
              color="no-background"
              className={styles.close}
              onClick={onClose}
              title="close"
            />

            {/* Editor content */}
            <div className={styles['editor-container']}>
              {children}
            </div>

            {/* Action buttons - bottom left */}
            <div className={styles['action-buttons']}>
              {actionButtons}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TinyMceModal;
